import React, { type FC, type PropsWithChildren } from 'react';

import { GatsbyImage } from 'gatsby-plugin-image';
import { withImage } from '@lucid/hooks';

import reactHTMLParser from '../../../utils/reactHTMLParser';
import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Text from '../../text/text';
import Card from '../../card/card';
import Image from '../../image/image';

export type IntegrationCardProps = Partial<Lucid.Marketplace.Integration> & {
  /** HTML content for the card */
  html?: string;
  /** Icon for the card */
  icon?: Lucid.Directus.Generated.Directus_Files;
  /** @returns true if integration was published less than 90 days ago */
  isNew?: boolean;
  /** Link for the card */
  link?: Lucid.Directus.Generated.Links;
};

const IntegrationCard: FC<PropsWithChildren<IntegrationCardProps>> = ({
  html,
  icon,
  icon48,
  isNew,
  link,
  shortDescription,
  title,
}) => (
  <Card
    shouldAnimateOnHover
    className="integrationCard"
    css={theme => ({
      label: 'integrationCard',
      color: theme.colors.text,
      fontWeight: theme.fontWeights.fw400,
      height: '100%',
    })}
  >
    <Box
      css={theme => ({
        display: 'flex',
        justifyContent: icon48 || icon ? 'space-between' : 'flex-end',
        alignItems: 'center',
        gap: theme.space.s16,
      })}
    >
      {icon48 && (
        <GatsbyImage
          alt={`${title} logo`}
          css={{ height: '48px', width: '48px' }}
          image={icon48.childImageSharp.gatsbyImageData}
          loading="lazy"
          objectFit="contain"
        />
      )}
      {icon && (
        <Image
          css={{ height: '48px', maxWidth: '240px' }}
          imageAlt={`${icon.title} logo`}
          imageSrc={withImage(icon)}
          loading="lazy"
        />
      )}
      {isNew && (
        <Text
          css={theme => ({
            background: theme.colors.blue[5],
            color: theme.colors.blue[90],
            margin: 0,
            flex: '0 1 130px',
            height: '25px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: theme.radii.r4,
          })}
          data-testid="integration-card-new-text"
          variant="interactiveXS500"
        >
          New
        </Text>
      )}
    </Box>
    <Card.Heading
      as="h3"
      css={theme => ({
        paddingTop: theme.space.s24,
      })}
      heading={title}
    />
    {shortDescription && (
      <Card.Text
        css={{
          marginTop: 0,
          marginBottom: 0,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        value={shortDescription}
      />
    )}
    {html && !shortDescription && (
      <Box
        key={title}
        css={{
          marginTop: 0,
          marginBottom: 0,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {reactHTMLParser(html)}
      </Box>
    )}
    <Card.Link
      accessibleDescription={`Go to ${title} page`}
      css={theme => ({
        margin: theme.space.s16,
      })}
      default_link_text={link?.default_link_text}
      final_url={link?.final_url}
      isFullCardLink={!link?.default_link_text}
    />
  </Card>
);

export { IntegrationCard };
export default IntegrationCard;
